import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import {
  TextField,
  Button,
  Modal,
  Box,
  Typography,
  Paper,
  Autocomplete,
  CircularProgress,
  IconButton
} from '@mui/material';
import { latinToCyrillic } from 'src/helper/helperFunction';
import { API_BASE_URL } from 'src/apiConfig';
import { useTranslation } from 'react-i18next';
import { useLocaleText } from 'src/helper/localeText';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmDeleteDialog from 'src/components/dialogs/delete/ConfirmDeleteDialog';
import { ListboxComponent } from '../users/components/helperFunction';

/* POCETNA STRANA ZA DMS */

const SchoolPay = () => {
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState(null);
  const [schools, setSchools] = useState([]);
  const [locations, setLocations] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEdit, setCurrentEdit] = useState({});
  const [loading, setLoading] = useState(true);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [errors, setErrors] = useState({});
  const { t } = useTranslation();
  const localeText = useLocaleText();

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        await fetchLocations();
        await fetchSchools();
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchAllData();
  }, []);

  useEffect(() => {
    if (schools.length > 0) {
      fetchData();
    }
  }, [schools]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/schoolPay.php`);
      const fetchedData = response.data;
      const updatedData = fetchedData.map((item) => {
        const matchingSchool = schools.find(
          (school) => school.name === item.school_name
        );
        return {
          ...item,
          id_skole: matchingSchool ? matchingSchool.id : null,
          zipcode: matchingSchool ? matchingSchool.zipcode : null
        };
      });
      setData(updatedData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/locations.php`);
      const fetchedLocations = response.data.map((loc) => ({
        ...loc,
        id: Number(loc.id),
        zipcode: loc.zipcode
      }));
      setLocations(fetchedLocations);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchSchools = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/schools.php`);
      const fetchedSchools = response.data.map((school) => ({
        ...school,
        zipcode: school.zipcode
      }));
      setSchools(fetchedSchools);
    } catch (error) {
      console.error('Error fetching schools:', error);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredData = data.filter(
    (item) =>
      latinToCyrillic(item.school_name.toLowerCase()).includes(
        latinToCyrillic(searchTerm.toLowerCase())
      ) && (locationFilter ? item.zipcode === locationFilter.zipcode : true)
  );

  const columns = [
    { field: 'school_name', headerName: t('columns.school'), width: 200 },
    { field: 'location_name', headerName: t('columns.location'), width: 150 },
    {
      field: 'broj_racuna',
      headerName: t('columns.accountNumber'),
      width: 150
    },
    {
      field: 'datum_placanja',
      headerName: t('columns.paymentDate'),
      width: 150
    },
    {
      field: 'broj_izvoda',
      headerName: t('columns.statementNumber'),
      width: 150
    },
    {
      field: 'student_num',
      headerName: t('columns.student_num'),
      width: 150
    },
    { field: 'napomena', headerName: t('columns.remark'), width: 200 },
    {
      field: 'actions',
      headerName: t('columns.actions'),
      width: 100,
      renderCell: (params) => (
        <>
          <IconButton onClick={() => handleEdit(params.row)} color="primary">
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              setDeleteId(params.row.id);
              setIsConfirmOpen(true);
            }}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </>
      )
    }
  ];

  const handleEdit = (row) => {
    setCurrentEdit({
      id: row.id,
      id_skole: row.id_skole,
      school_name: row.school_name,
      zipcode: row.zipcode,
      broj_racuna: row.broj_racuna || '',
      datum_placanja: row.datum_placanja || '',
      broj_izvoda: row.broj_izvoda || '',
      napomena: row.napomena || ''
    });
    setErrors({});
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/api/schoolPay.php?id=${deleteId}`);
      fetchData();
      setIsConfirmOpen(false);
    } catch (error) {
      console.error('Error deleting data:', error);
    }
  };

  const handleAddNew = () => {
    setCurrentEdit({
      id: null,
      id_skole: null,
      school_name: '',
      zipcode: null,
      broj_racuna: '',
      datum_placanja: '',
      broj_izvoda: '',
      napomena: ''
    });
    setErrors({});
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    let newErrors = {};
    if (!currentEdit.id_skole) {
      newErrors.id_skole = t('validation.requiredField');
    }
    if (!currentEdit.datum_placanja) {
      newErrors.datum_placanja = t('validation.requiredField');
    } else {
      const selectedDate = new Date(currentEdit.datum_placanja);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate > today) {
        newErrors.datum_placanja = t('validation.futureDateError');
      }
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const payload = {
        id_skole: currentEdit.id_skole,
        broj_racuna: currentEdit.broj_racuna,
        datum_placanja: currentEdit.datum_placanja,
        broj_izvoda: currentEdit.broj_izvoda,
        napomena: currentEdit.napomena
      };

      if (currentEdit.id) {
        await axios.put(
          `${API_BASE_URL}/api/schoolPay.php?id=${currentEdit.id}`,
          payload
        );
      } else {
        await axios.post(`${API_BASE_URL}/api/schoolPay.php`, payload);
      }
      fetchData();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving data:', error);
    }
  };

  const filterSchoolOptions = (options, { inputValue }) => {
    const cyrillicInput = latinToCyrillic(inputValue.toLowerCase());

    return options.filter((option) =>
      latinToCyrillic(option.name.toLowerCase()).includes(cyrillicInput)
    );
  };

  const filterLocationOptions = (options, { inputValue }) => {
    const cyrillicInput = latinToCyrillic(inputValue.toLowerCase());

    return options.filter((option) =>
      latinToCyrillic(option.name.toLowerCase()).includes(cyrillicInput)
    );
  };

  return (
    <Box
      sx={{
        padding: 3,
        backgroundColor: '#f5f5f5',
        borderRadius: '15px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        maxWidth: '1800px'
      }}
    >
      <Paper
        elevation={3}
        sx={{
          padding: '20px',
          borderRadius: '12px'
        }}
      >
        <TextField
          label={t('search.placeholder')}
          value={searchTerm}
          onChange={handleSearch}
          variant="outlined"
          sx={{ marginBottom: '20px', width: '100%' }}
        />
        <Autocomplete
          options={locations}
          getOptionLabel={(option) => option.name || ''}
          value={locationFilter}
          onChange={(event, newValue) => setLocationFilter(newValue)}
          filterOptions={filterLocationOptions}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('filters.location')}
              variant="outlined"
              sx={{ marginBottom: '20px', width: '100%' }}
            />
          )}
          noOptionsText={t('pagination.noRowsLabel')}
        />
        <Button
          variant="contained"
          onClick={handleAddNew}
          sx={{
            marginBottom: '20px',
            backgroundColor: '#2596be',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#1e7aa0'
            }
          }}
        >
          {t('dialog.submitButton')}
        </Button>
        {loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '20px'
            }}
          >
            <CircularProgress />
          </div>
        ) : (
          <DataGrid
            rows={filteredData}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            autoHeight
            disableColumnMenu
            localeText={localeText}
            componentsProps={{
              pagination: {
                labelRowsPerPage: t('pagination.rowsPerPage'),
                labelDisplayedRows: ({ from, to, count }) =>
                  `${from}-${to} ${t('pagination.of')} ${
                    count !== -1 ? count : `${t('pagination.moreThan')} ${to}`
                  }`
              }
            }}
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#2596be',
                color: '#ffffff',
                fontSize: '16px'
              },
              '& .MuiDataGrid-cell': {
                padding: '12px',
                fontSize: '14px'
              },
              '& .MuiDataGrid-footerContainer': {
                backgroundColor: '#2596be',
                color: '#ffffff'
              }
            }}
          />
        )}
        <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: 24,
              padding: '20px',
              width: '400px'
            }}
          >
            <Typography variant="h6" mb={2}>
              {currentEdit.id
                ? t('dialog.editPayment')
                : t('dialog.addPayment')}
            </Typography>
            <Autocomplete
              options={locations}
              getOptionLabel={(option) => option.name || ''}
              ListboxComponent={ListboxComponent}
              isOptionEqualToValue={(option, value) =>
                option.zipcode === value.zipcode
              }
              value={
                locations.find((loc) => loc.zipcode === currentEdit.zipcode) ||
                null
              }
              onChange={(event, newValue) => {
                setCurrentEdit({
                  ...currentEdit,
                  zipcode: newValue ? newValue.zipcode : null,
                  id_skole: null,
                  school_name: ''
                });
                setErrors({ ...errors, id_skole: null });
              }}
              filterOptions={filterLocationOptions}
              renderOption={(props, option) => (
                <li {...props} key={option.zipcode}>
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('form.location')}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                />
              )}
              noOptionsText={t('pagination.noRowsLabel')}
            />
            <Autocomplete
              options={
                currentEdit.zipcode
                  ? schools.filter(
                      (school) => school.zipcode === currentEdit.zipcode
                    )
                  : []
              }
              getOptionLabel={(option) => option.name || ''}
              value={schools.find((s) => s.id === currentEdit.id_skole) || null}
              onChange={(event, newValue) => {
                setCurrentEdit({
                  ...currentEdit,
                  id_skole: newValue ? newValue.id : null,
                  school_name: newValue ? newValue.name : ''
                });
                setErrors({ ...errors, id_skole: null });
              }}
              filterOptions={filterSchoolOptions}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('form.school')}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  required
                  error={Boolean(errors.id_skole)}
                  helperText={errors.id_skole}
                  disabled={!currentEdit.zipcode}
                />
              )}
              noOptionsText={t('pagination.noRowsLabel')}
            />
            <TextField
              label={t('form.accountNumber')}
              value={currentEdit.broj_racuna || ''}
              onChange={(e) =>
                setCurrentEdit({
                  ...currentEdit,
                  broj_racuna: e.target.value
                })
              }
              fullWidth
              margin="normal"
            />
            <TextField
              label={t('form.paymentDate')}
              type="date"
              value={currentEdit.datum_placanja || ''}
              onChange={(e) => {
                const selectedDate = new Date(e.target.value);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                let errorMessage = '';
                if (e.target.value === '') {
                  errorMessage = t('validation.requiredField');
                } else if (selectedDate > today) {
                  errorMessage = t('validation.futureDateError');
                }
                setErrors({
                  ...errors,
                  datum_placanja: errorMessage
                });
                setCurrentEdit({
                  ...currentEdit,
                  datum_placanja: e.target.value
                });
              }}
              error={Boolean(errors.datum_placanja)}
              helperText={errors.datum_placanja}
              fullWidth
              margin="normal"
              InputLabelProps={{
                shrink: true
              }}
              required
            />
            <TextField
              label={t('form.statementNumber')}
              value={currentEdit.broj_izvoda || ''}
              onChange={(e) =>
                setCurrentEdit({
                  ...currentEdit,
                  broj_izvoda: e.target.value
                })
              }
              fullWidth
              margin="normal"
            />
            <TextField
              label={t('form.note')}
              value={currentEdit.napomena || ''}
              onChange={(e) =>
                setCurrentEdit({ ...currentEdit, napomena: e.target.value })
              }
              fullWidth
              margin="normal"
            />
            <Box mt={2} display="flex" justifyContent="flex-end">
              <Button
                onClick={() => setIsModalOpen(false)}
                sx={{ marginRight: '10px' }}
              >
                {t('buttons.cancel')}
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{
                  backgroundColor: '#2596be'
                }}
              >
                {t('dialog.save')}
              </Button>
            </Box>
          </Box>
        </Modal>
        <ConfirmDeleteDialog
          open={isConfirmOpen}
          onClose={() => setIsConfirmOpen(false)}
          onConfirm={handleDelete}
        />
      </Paper>
    </Box>
  );
};

export default SchoolPay;
